import { useState } from "react";
import { toast } from "sonner";
import { StatusPill, fmtTime } from "@/components/kit";
import { field, ghostBtn, primaryBtn } from "@/lib/ops";

const H = ({ children }) => <h3 className="mb-2 mt-6 text-[11px] font-bold uppercase tracking-[0.16em] text-[var(--cc-navy)]">{children}</h3>;

export const Criteria = ({ t, act }) => (
  <>
    <H>Acceptance criteria</H>
    {!t.acceptance_criteria.length && <p className="text-xs text-[var(--cc-muted)]">None defined.</p>}
    <ul className="space-y-1.5" data-testid="task-criteria">
      {t.acceptance_criteria.map((c, i) => (
        <li key={i} className="flex items-start gap-2 text-sm">
          <input type="checkbox" data-testid={`criterion-${i}`} checked={c.met} disabled={["done", "cancelled"].includes(t.status)}
            onChange={() => act("patch", { acceptance_criteria: t.acceptance_criteria.map((x, j) => (j === i ? { ...x, met: !x.met } : x)) })} />
          <span className={c.met ? "text-[var(--cc-muted)] line-through" : ""}>{c.text}</span>
        </li>
      ))}
    </ul>
  </>
);

export const Evidence = ({ t, act, me }) => {
  const [f, setF] = useState({ kind: "test_report", summary: "", url: "" });
  const add = async (e) => { e.preventDefault(); if (await act("evidence", { ...f, url: f.url || null })) setF({ ...f, summary: "", url: "" }); };
  return (
    <>
      <H>Evidence &amp; verification</H>
      <ul className="space-y-2" data-testid="task-evidence">
        {t.evidence.map((e) => (
          <li key={e.id} className="rounded-sm border border-[var(--cc-line)] p-2.5 text-xs" data-testid={`evidence-${e.id}`}>
            <div className="flex items-center justify-between gap-2"><span className="font-mono">{e.kind}</span>
              {e.verification ? <StatusPill status={e.verification.verdict === "pass" ? "passed" : "failed"} label={`${e.verification.verdict}${e.verification.owner_attestation ? " · attested" : ""}`} /> : <StatusPill status="pending" label="unverified" />}</div>
            <p className="mt-1 text-sm">{e.summary}</p>
            {e.url && <a href={e.url} target="_blank" rel="noreferrer" className="break-all text-[var(--cc-info)] underline">{e.url}</a>}
            <p className="mt-1 text-[var(--cc-muted)]">by {e.added_by} · {fmtTime(e.at)}{e.verification && ` · verified by ${e.verification.by}`}</p>
            {!e.verification && !["done", "cancelled"].includes(t.status) && (
              <div className="mt-2 flex gap-2">
                <button data-testid={`evidence-pass-${e.id}`} className={ghostBtn} onClick={() => act("verify", { evidence_id: e.id, verdict: "pass" })}>Verify pass</button>
                <button data-testid={`evidence-fail-${e.id}`} className={ghostBtn} onClick={() => act("verify", { evidence_id: e.id, verdict: "fail" })}>Fail</button>
              </div>)}
          </li>))}
      </ul>
      {!["done", "cancelled"].includes(t.status) && (
        <form onSubmit={add} className="mt-2 grid gap-2 sm:grid-cols-[120px_1fr]" data-testid="evidence-form">
          <select data-testid="evidence-kind" className={field} value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>{["test_report", "qa_result", "screenshot", "link", "log", "commit", "note"].map((k) => <option key={k}>{k}</option>)}</select>
          <input data-testid="evidence-summary" className={field} placeholder="What does this evidence show?" value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} required />
          <input data-testid="evidence-url" className={`${field} sm:col-span-2`} placeholder="Link (optional)" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} />
          <button data-testid="evidence-add" className={`${primaryBtn} sm:col-span-2`}>Attach evidence</button>
        </form>)}
      <p className="mt-2 text-[11px] text-[var(--cc-muted)]">Completion requires evidence verified by someone other than the owner{me && ""}.</p>
    </>
  );
};

export const History = ({ t }) => (
  <>
    <H>Audit history</H>
    <ol className="space-y-1.5 border-l border-[var(--cc-line)] pl-3" data-testid="task-history">
      {[...t.history].reverse().map((h, i) => (
        <li key={i} className="text-[11px]"><span className="font-mono text-[var(--cc-muted)]">#{h.audit_seq} · {fmtTime(h.at)}</span><br />
          <b className="font-medium">{h.action}</b> by {h.actor} {h.details && <span className="text-[var(--cc-muted)]">{JSON.stringify(h.details).slice(0, 140)}</span>}</li>))}
    </ol>
  </>
);

export const TicketPanel = ({ t, act, onOpen }) => {
  const [c, setC] = useState({ direction: "outbound", channel: "email", body: "" });
  const [r, setR] = useState({ summary: "", evidence_summary: "" });
  const [x, setX] = useState({ title: "", technical_summary: "" });
  const tk = t.ticket;
  if (tk.redacted) return <p className="mt-4 text-xs">Customer details are hidden from this department.</p>;
  return (
    <div data-testid="ticket-panel">
      <H>Customer ticket</H>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <p>Severity <b data-testid="ticket-severity">{tk.severity}</b></p><p>Customer <b data-testid="ticket-customer">{tk.customer_ref}</b></p>
        <p>Response due <b>{fmtTime(tk.response_due)}</b></p><p>First response <b>{fmtTime(tk.first_response_at)}</b></p>
        {tk.customer_contact && <p className="col-span-2">Contact {tk.customer_contact}</p>}
      </div>
      <H>Communication history</H>
      <ul className="space-y-1.5" data-testid="ticket-comms">{tk.communications.map((m) => <li key={m.id} className="rounded-sm bg-[var(--cc-paper)] p-2 text-xs"><span className="font-mono text-[var(--cc-muted)]">{m.direction} · {m.channel} · {fmtTime(m.at)}</span><p className="mt-0.5 whitespace-pre-wrap">{m.body}</p></li>)}</ul>
      <form className="mt-2 space-y-2" onSubmit={async (e) => { e.preventDefault(); if (await act("comm", c)) setC({ ...c, body: "" }); }}>
        <div className="flex gap-2"><select data-testid="comm-direction" className={field} value={c.direction} onChange={(e) => setC({ ...c, direction: e.target.value })}>{["outbound", "inbound", "internal"].map((d) => <option key={d}>{d}</option>)}</select>
          <select data-testid="comm-channel" className={field} value={c.channel} onChange={(e) => setC({ ...c, channel: e.target.value })}>{["email", "phone", "chat", "in_app", "note"].map((d) => <option key={d}>{d}</option>)}</select></div>
        <textarea data-testid="comm-body" rows={2} className={field} placeholder="Message" value={c.body} onChange={(e) => setC({ ...c, body: e.target.value })} required />
        <button data-testid="comm-add" className={primaryBtn}>Log communication</button>
      </form>
      <H>Resolution</H>
      {tk.resolution ? <p className="text-sm" data-testid="ticket-resolution">{tk.resolution.summary} <span className="text-xs text-[var(--cc-muted)]">· {tk.resolution.by}</span></p> : (
        <form className="space-y-2" onSubmit={(e) => { e.preventDefault(); act("resolve", r); }}>
          <input data-testid="resolution-summary" className={field} placeholder="Resolution summary" value={r.summary} onChange={(e) => setR({ ...r, summary: e.target.value })} required />
          <input data-testid="resolution-evidence" className={field} placeholder="Resolution evidence" value={r.evidence_summary} onChange={(e) => setR({ ...r, evidence_summary: e.target.value })} required />
          <button data-testid="resolution-submit" className={primaryBtn}>Record resolution</button>
        </form>)}
      <H>Escalate to Development</H>
      <form className="space-y-2" onSubmit={async (e) => { e.preventDefault(); const d = await act("escalate", x); if (d) { toast.success(`Linked ${d.id} created`); onOpen(d.id); } }}>
        <input data-testid="escalate-title" className={field} placeholder="Development task title" value={x.title} onChange={(e) => setX({ ...x, title: e.target.value })} required />
        <textarea data-testid="escalate-summary" rows={2} className={field} placeholder="Technical summary (no customer personal data)" value={x.technical_summary} onChange={(e) => setX({ ...x, technical_summary: e.target.value })} required />
        <button data-testid="escalate-submit" className={primaryBtn}>Create linked Development task</button>
      </form>
    </div>
  );
};
